/**
 * Opt-in image-attachment host capability for the Usable Chat embed.
 *
 * Some hosts have a tool that returns an image blob (PowerPoint's
 * `get_slide_screenshot` returns a base64 PNG). Rather than hand the AI a giant
 * unusable base64 string, the image is delivered to the chat as a proper
 * attachment via the embed's `addImage()` and the blob is stripped from the tool
 * result. This is an opt-in extension, not a fork: Excel/Word ignore it entirely
 * (they never read the capabilities argument), exactly as CLAUDE.md prescribes —
 * "host capabilities are opt-in extensions, not forks".
 */

/**
 * Capabilities handed to an app's tool handler for a single tool call. The shell
 * threads these from the live embed instance; an app uses them only if it needs
 * them (PowerPoint does, Excel/Word don't).
 */
import type { AttachmentsApi, StagedDataApi, WorkspaceFilesApi } from "./embed-sdk";

export interface ToolCallCapabilities {
  /**
   * Data-staging RPC surface (list / readBytes of paperclip uploads). Present
   * when the embed exposes data staging to the parent; hosts use it to pull
   * e.g. an uploaded logo's bytes.
   */
  stagedData?: StagedDataApi;
  /**
   * Normal chat attachments RPC (list / readBytes of paperclip uploads that
   * STAY multimodal). Present when the embed exposes attachments to the
   * parent (features.attachments.exposeToParent, v1.171.0).
   */
  attachments?: AttachmentsApi;
  /**
   * Usable WORKSPACE file reads (fileId from the chat's own search-files).
   * Present when features.workspaceFiles.exposeToParent is on (v1.172.0).
   */
  workspaceFiles?: WorkspaceFilesApi;
  /**
   * Deliver an image to the chat (e.g. a slide thumbnail). `submit: true`
   * auto-submits it as a user turn so the model sees it and reviews it next turn
   * (the self-verify path); otherwise it only stages into the composer.
   */
  addImage(
    base64OrDataUrl: string,
    options?: { mimeType?: string; fileName?: string; submit?: boolean }
  ): void;
  /** The embed's requestId for this tool call (correlation / host-side dedup). */
  requestId: string;
  /**
   * Acquire a Microsoft Graph access token (opt-in, Outlook only). Present only
   * when the app is bootstrapped with a `graph` config; other hosts leave it
   * undefined. Silent-first — throws `GraphConsentRequired` (mapped centrally to an
   * `interaction_required` tool result) when the user hasn't connected Microsoft
   * 365. The token is used in-pane by the tool handler and NEVER returned to the
   * model / crosses the chat bridge — only the Graph *results* do. See graph-auth.ts.
   */
  acquireGraphToken?(scopes: string[], options?: { interactive?: boolean }): Promise<string>;
}

/** Maps a tool whose result carries an image blob to how it's delivered + stripped. */
export interface ImageAttachmentRule {
  /** Tool whose result holds an image blob to surface as a chat attachment. */
  tool: string;
  /** Result field holding the base64 image (default "base64"). */
  field?: string;
  /** MIME type passed to addImage (default "image/png"). */
  mimeType?: string;
  /** Attachment file name passed to addImage (default "slide.png", matching embed-sdk's addImage default). */
  fileName?: string;
  /**
   * Auto-submit the delivered image as a user turn so the model reviews it on the
   * next turn (the self-verify loop). Default false (stage-only). Set true for a
   * screenshot tool whose whole purpose is the AI seeing its own output.
   */
  submit?: boolean;
}

// Result objects for which the image has already been delivered. The shared
// execution layer (tool-execution.ts) dedups a double-mount re-delivery by its
// requestId — re-delivery of one TOOL_CALL carries the SAME requestId — and
// returns the *same cached result object* for it, so a WeakSet keyed on the
// result reliably fires addImage exactly once without a TTL or content hash, and
// without needing the requestId itself here.
//
// COUPLING NOTE: this once-only guarantee holds only while the shared dedup
// returns the *same* result object for the duplicate — i.e. while the duplicate
// is in-flight or within its completed-result TTL (tool-execution's
// COMPLETED_TTL_MS, 5 s). A duplicate that somehow fired after that window would
// re-execute the handler, yield a fresh result object, miss this WeakSet, and
// deliver a second (cosmetic) thumbnail. No data loss either way.
const deliveredResults = new WeakSet<object>();

/**
 * Post-process a tool result against a set of image-attachment rules: if a rule
 * matches the tool and the result carries a base64 image blob, deliver the image
 * to the chat via `caps.addImage` (once per result object) and return the result
 * with the blob field stripped. Non-matching tools/results pass through
 * untouched. Pure aside from the addImage side effect and the dedup WeakSet.
 */
export function deliverImageAttachment(
  tool: string,
  result: unknown,
  caps: ToolCallCapabilities | undefined,
  rules: ImageAttachmentRule[]
): unknown {
  const rule = rules.find((r) => r.tool === tool);
  if (!rule) return result;

  const field = rule.field ?? "base64";
  if (result === null || typeof result !== "object" || !(field in result)) return result;
  const blob = (result as Record<string, unknown>)[field];
  if (typeof blob !== "string") return result;

  // Deliver once per result object (dedups the double-mount duplicate call); strip
  // the blob on every call (first and duplicate) so the AI never receives it.
  if (caps && !deliveredResults.has(result as object)) {
    deliveredResults.add(result as object);
    caps.addImage(blob, {
      mimeType: rule.mimeType ?? "image/png",
      fileName: rule.fileName ?? "slide.png",
      submit: rule.submit ?? false,
    });
  }
  const { [field]: _blob, ...rest } = result as Record<string, unknown>;
  return rest;
}
